import { homepageRoute } from '@/modules/Homepage/route';
import { useUserStore } from '@/stores/userStore';
import { UserManager, type UserManagerSettings } from 'oidc-client-ts';
import { NavigationGuard } from 'vue-router';

if (!import.meta.env.VITE_IAM_ISSUER) {
  throw new Error('VITE_IAM_ISSUER is not defined');
}

if (!import.meta.env.VITE_IAM_CLIENT_ID) {
  throw new Error('VITE_IAM_CLIENT_ID is not defined');
}

const settings = {
  scope: 'openid email profile',
  loadUserInfo: true,
  authority: import.meta.env.VITE_IAM_ISSUER,
  client_id: import.meta.env.VITE_IAM_CLIENT_ID,
  redirect_uri: `${window.origin}/callback`,
  post_logout_redirect_uri: window.origin,
} as const satisfies UserManagerSettings;

export const authManager = new UserManager(settings);

export const AUTH_GUARD: NavigationGuard = async (to) => {
  if (!to.matched.some((record) => record.meta.requiresAuth)) return;

  const userStore = useUserStore();
  await userStore.fetch();

  if (userStore.isInvalidUserSession()) {
    userStore.user = null;
    const redirectInfo = {
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    };
    sessionStorage.setItem('redirectInfo', JSON.stringify(redirectInfo));
    await authManager.signinRedirect();
  }
};

export const AUTH_CALLBACK_GUARD: NavigationGuard = async (to) => {
  const redirectInfoStr = sessionStorage.getItem('redirectInfo');
  sessionStorage.removeItem('redirectInfo');
  if ('state' in to.query) {
    await authManager.signinCallback(window.location.href);
    const userStore = useUserStore();
    await userStore.fetch();
  }

  if (redirectInfoStr) {
    const redirectInfo = JSON.parse(redirectInfoStr);
    const urlSearchParams = new URLSearchParams(redirectInfo.search);
    const queryParams = Object.fromEntries(urlSearchParams.entries());

    return {
      path: redirectInfo.pathname,
      query: queryParams,
      hash: redirectInfo.hash,
    };
  }

  return { path: homepageRoute.path }; // redirects to home route if users accesses /callback manually
};
